import { defineStore } from 'pinia';
import { ref } from 'vue';
import moment from 'moment';
import { Prefixes } from '@/service/Constants';
import UserAuthenticationService from '@/service/UserAuthenticationService';
import { refreshToken } from '@/service/RefreshTokenService';
import { getExpiryTime, getWarningTime, getActiveTime } from '@/service/AutoLogoutService';
import { useUserStore } from '@/stores/user-store';
import { useSessionStore } from '@/stores/sessionStore';

export const useAutoLogout = defineStore(
    'auto-logout',
    () => {
        const
            userStore = useUserStore(),
            sessionStore = useSessionStore();

        const expiryTime = ref(getExpiryTime());
        const warningTime = ref(getWarningTime());
        const timer = ref(0);
        const timerInterval = ref(null);
        const warningTimer = ref(expiryTime.value - warningTime.value);
        const warningTimerInterval = ref(warningTimer);
        const userService = new UserAuthenticationService();

        const activeTimer = ref(getActiveTime())
        const activeInterval = ref()
        const isUserIdle = ref(false)

        /** State to determine if the time reached the warning time */
        const isTimeWarned = ref(false);

        /** Show idle warning modal which should trigger across tabs */
        const showIdleWarningModal = ref(false);

        /** Countdown interval reference for cleanup */
        const countdownInterval = ref(null);

        /** Global timer sync key */
        const TIMER_SYNC_KEY = 'al-global-timer';
        const WARNING_STATE_KEY = 'al-warning-state';
        const MODAL_STATE_KEY = 'al-modal-state';

        /**
         * Initialize and sync with other tabs on store creation
         */
        const initializeFromStorage = () => {
            // Check if the warning is already active from another tab
            const existingWarningState = localStorage.getItem(Prefixes.nimbusLocalStorage + WARNING_STATE_KEY);
            const existingModalState = localStorage.getItem(Prefixes.nimbusLocalStorage + MODAL_STATE_KEY);
            const globalTimer = localStorage.getItem(Prefixes.nimbusLocalStorage + TIMER_SYNC_KEY);

            if (existingWarningState) {
                const warningData = JSON.parse(existingWarningState);
                isTimeWarned.value = warningData.isWarned;
                if (warningData.warningStartTime) {
                    // Calculate the remaining warning time
                    const elapsed = Math.floor((Date.now() - warningData.warningStartTime) / 1000);
                    const remaining = warningTimer.value - elapsed;
                    warningTimerInterval.value = Math.max(0, remaining);
                }
            }

            if (existingModalState) {
                showIdleWarningModal.value = JSON.parse(existingModalState);
            }

            if (globalTimer) {
                const timerData = JSON.parse(globalTimer);
                // Sync timer with other tabs
                const elapsed = Math.floor((Date.now() - timerData.timestamp) / 1000);
                timer.value = Math.min(timerData.value + elapsed, expiryTime.value);

                if (timer.value >= expiryTime.value) {
                    logoutActiveUser('idle');
                    return;
                }
            }

            // Start listening to storage events immediately
            initStorageListener();
        };

        /**
         * Set the Timer from the Account Settings
         */
        const setSettingsTimer = () => {
            expiryTime.value = userStore.getALExpirationTime();
            warningTimer.value = parseInt(expiryTime.value - warningTime.value);
            warningTimerInterval.value = warningTimer.value;
        };

        /**
         * Record user activity and broadcast to other tabs
         */
        const recordUserActivity = () => {
            if (showIdleWarningModal.value) {
                return;
            }
            localStorage.setItem(Prefixes.nimbusLocalStorage + 'al-last-activity', Date.now());
            resetTimer();
        };

        /**
         * Sync timer state across tabs
         */
        const syncTimerState = () => {
            const timerData = {
                value: timer.value,
                timestamp: Date.now()
            };
            localStorage.setItem(Prefixes.nimbusLocalStorage + TIMER_SYNC_KEY, JSON.stringify(timerData));
        };

        /**
         * Sync warning state across tabs
         */
        const syncWarningState = () => {
            const warningData = {
                isWarned: isTimeWarned.value,
                warningStartTime: isTimeWarned.value ? Date.now() : null,
                remainingTime: warningTimerInterval.value
            };
            localStorage.setItem(Prefixes.nimbusLocalStorage + WARNING_STATE_KEY, JSON.stringify(warningData));
        };

        /**
         * Sync modal state across tabs
         */
        const syncModalState = () => {
            localStorage.setItem(Prefixes.nimbusLocalStorage + MODAL_STATE_KEY, JSON.stringify(showIdleWarningModal.value));
        };

        /**
         * Dismiss idle warning across all tabs
         */
        const dismissIdleWarning = () => {
            // Clear countdown if running
            if (countdownInterval.value) {
                clearInterval(countdownInterval.value);
                countdownInterval.value = null;
            }

            // Reset warning states
            isTimeWarned.value = false;
            showIdleWarningModal.value = false;
            
            // Sync states across tabs
            syncWarningState();
            syncModalState();
            
            // Additional cleanup signal
            localStorage.setItem(Prefixes.nimbusLocalStorage + 'al-warning-dismissed', Date.now());

            resetStoreValues();
            autoStartTimer();
            setActiveTimer();
        };

        // function to refresh the values
        const staySignedIn = async () => {
            await refreshToken();
            localStorage.setItem(Prefixes.nimbusLocalStorage + 'al-stay-logged-in', JSON.stringify(moment()));
            dismissIdleWarning();
        };

        // function to request in API to clear the token and log out the user
        const logoutActiveUser = async (reason = 'idle') => {
            await sessionStore.logout(reason);
            resetStoreValues();
        };

        const handleIdleModalChoice = async (stayLoggedIn) => {
            if (stayLoggedIn) {
                await staySignedIn();
            } else {
                await logoutActiveUser('manual');
            }
        };

        // function to start the timer
        const autoStartTimer = () => {
            clearTimerInterval();
            timerInterval.value = window.setInterval(() => {
                if (!userService.isAuthenticated()) {
                    clearTimerInterval();
                    resetStoreValues();
                    return;
                }

                // Check if another tab has recent activity
                const lastActivity = localStorage.getItem(Prefixes.nimbusLocalStorage + 'al-last-activity');
                if (lastActivity && (Date.now() - lastActivity) < 5000) {
                    resetTimer();
                    return;
                }

                timer.value++;
                
                // Sync timer state every 5 seconds to reduce localStorage writes
                if (timer.value % 5 === 0) {
                    syncTimerState();
                }

                // check for a time-warned interval
                if (!isTimeWarned.value && warningTime.value <= timer.value) {
                    console.log('Warning time reached, triggering modal');
                    isTimeWarned.value = true;
                    isUserIdle.value = true;
                    showIdleWarningModal.value = true;

                    // Sync warning and modal states across tabs
                    syncWarningState();
                    syncModalState();

                    // Also set the legacy localStorage keys for backward compatibility
                    localStorage.setItem(Prefixes.nimbusLocalStorage + 'al-is-timer-warned', 'true');

                    clearInterval(timerInterval.value);

                    // start the timer for auto-logout
                    startIdleCountdown();
                    return;
                }
            }, 1000);
        };

        const startIdleCountdown = () => {
            countdownInterval.value = setInterval(async () => {
                // Check if the warning was dismissed by another tab
                const warningDismissed = localStorage.getItem(Prefixes.nimbusLocalStorage + 'al-warning-dismissed');
                const lastActivity = localStorage.getItem(Prefixes.nimbusLocalStorage + 'al-last-activity');
                
                if ((warningDismissed && (Date.now() - warningDismissed) < 2000) ||
                    (lastActivity && (Date.now() - lastActivity) < 5000)) {
                    if (showIdleWarningModal.value) {
                        return;
                    }
                    dismissIdleWarning();
                    return;
                }

                if (warningTimerInterval.value > 0) {
                    warningTimerInterval.value--;
                    
                    // Sync countdown state every second for real-time sync
                    syncWarningState();
                } else {
                    clearInterval(countdownInterval.value);
                    await logoutActiveUser('idle');
                }
            }, 1000);
        };

        // Clear data related to the timer
        const resetTimer = (on_hide_event = false) => {
            if (!isTimeWarned.value || on_hide_event || timer.value < warningTime.value) {
                clearTimerInterval();
                clearActiveInterval();
                resetStoreValues();
                syncTimerState(); // Sync reset state
                autoStartTimer();
                setActiveTimer();
            }
        };

        // Force stop the timer
        const clearTimerInterval = () => {
            clearInterval(timerInterval.value);
        };

        // Reset values
        const resetStoreValues = () => {
            timer.value = 0;
            warningTimer.value = expiryTime.value - warningTime.value;
            warningTimerInterval.value = warningTimer.value;
            isTimeWarned.value = false;
            showIdleWarningModal.value = false;
            isUserIdle.value = false;
            
            // Clear sync states
            localStorage.removeItem(Prefixes.nimbusLocalStorage + TIMER_SYNC_KEY);
            localStorage.removeItem(Prefixes.nimbusLocalStorage + WARNING_STATE_KEY);
            localStorage.removeItem(Prefixes.nimbusLocalStorage + MODAL_STATE_KEY);
        };

        // Function to remove persisted local storage used in auto logout
        const removeLSInAutoLogout = () => {
            const lsItems = [
                'al-stay-logged-in',
                'al-is-timer-warned',
                'al-auto-logout',
                'al-warning-timer',
                'al-show-idle-modal',
                'al-last-activity',
                'al-warning-dismissed',
                TIMER_SYNC_KEY,
                WARNING_STATE_KEY,
                MODAL_STATE_KEY,
            ];

            lsItems.forEach((item) => {
                localStorage.removeItem(Prefixes.nimbusLocalStorage + item);
            });

            resetStoreValues();
        };

        // Reset the active values
        const resetActiveTimerValues = () => {
            activeTimer.value = getActiveTime();
            isUserIdle.value = false;
        };

        const setActiveTimer = () => {
            clearActiveInterval();

            activeInterval.value = window.setInterval(() => {
                if (!userService.isAuthenticated()) return;
                if (activeTimer.value > 0) {
                    activeTimer.value--;
                } else {
                    clearInterval(activeInterval.value);
                    if (!isUserIdle.value) {
                        triggerRefreshToken();
                    }
                }
            }, 1000);
        };

        const triggerRefreshToken = async () => {
            await refreshToken();
            activeTimer.value = getActiveTime();
            isUserIdle.value = false;
            setActiveTimer();
        };

        const clearActiveInterval = () => {
            clearInterval(activeInterval.value);
        };

        const initStorageListener = () => {
            window.addEventListener('storage', (event) => {
                const key = event.key;
                
                if (key === Prefixes.nimbusLocalStorage + MODAL_STATE_KEY) {
                    showIdleWarningModal.value = JSON.parse(event.newValue || 'false');
                }
                
                if (key === Prefixes.nimbusLocalStorage + WARNING_STATE_KEY) {
                    if (event.newValue) {
                        const warningData = JSON.parse(event.newValue);
                        isTimeWarned.value = warningData.isWarned;
                        if (warningData.remainingTime !== undefined) {
                            warningTimerInterval.value = warningData.remainingTime;
                        }
                    } else {
                        isTimeWarned.value = false;
                    }
                }

                if (key === Prefixes.nimbusLocalStorage + TIMER_SYNC_KEY) {
                    if (event.newValue) {
                        const timerData = JSON.parse(event.newValue);
                        const elapsed = Math.floor((Date.now() - timerData.timestamp) / 1000);
                        timer.value = Math.min(timerData.value + elapsed, expiryTime.value);
                    }
                }
                
                if (key === Prefixes.nimbusLocalStorage + 'al-warning-dismissed') {
                    dismissIdleWarning();
                }

                if (key === Prefixes.nimbusLocalStorage + 'al-last-activity') {
                    const lastActivity = parseInt(event.newValue);
                    if (lastActivity && (Date.now() - lastActivity) < 5000) {
                        if (showIdleWarningModal.value) {
                            return;
                        }
                        resetTimer();
                    }
                }
            });

            // Add activity listeners for the current tab
            const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
            
            const throttledActivity = throttle(() => {
                recordUserActivity();
            }, 1000);

            activityEvents.forEach(event => {
                document.addEventListener(event, throttledActivity, true);
            });
        };

        // Simple throttle function
        const throttle = (func, delay) => {
            let timeoutId;
            let lastExecTime = 0;
            return function (...args) {
                const currentTime = Date.now();
                
                if (currentTime - lastExecTime > delay) {
                    func.apply(this, args);
                    lastExecTime = currentTime;
                } else {
                    clearTimeout(timeoutId);
                    timeoutId = setTimeout(() => {
                        func.apply(this, args);
                        lastExecTime = Date.now();
                    }, delay - (currentTime - lastExecTime));
                }
            };
        };

        // Initialize from storage when a store is created
        initializeFromStorage();

        return {
            /**
             * States
             */
            timer,
            expiryTime,
            warningTime,
            activeTimer,
            isUserIdle,
            isTimeWarned,
            warningTimerInterval,
            showIdleWarningModal,

            /**
             * Getters
             */
            warningTimer,

            /**
             * Actions
             */
            setSettingsTimer,
            staySignedIn,
            logoutActiveUser,
            autoStartTimer,
            resetTimer,
            resetStoreValues,
            removeLSInAutoLogout,
            setActiveTimer,
            clearActiveInterval,
            resetActiveTimerValues,
            clearTimerInterval,
            handleIdleModalChoice,
            initStorageListener,
            recordUserActivity,
            dismissIdleWarning,
        };
    },
    {
        persist: {
            key: 'auto-logout-store',
            // Don't persist timers and intervals, but persist basic config
            pick: ['expiryTime', 'warningTime']
        },
        share: {
            enable: false,
        },
    }
);
